/**
 * LEAD-360-CONFORMANCE-B1/B2 -- NEXT ACTION EDGE (the RECORD 360 archetype's
 * "NEXT band"; docs/design-studio/lead-360/APPROVED-DIRECTION-SYSTEM-EXTRACT.md
 * TIER 2 "COMPOSER ON INVOCATION" + NEXT-LINE DOMINANCE; F1 NEW PATTERNS
 * item 3 candidate "NextActionEdge").
 *
 * A left-accented edge card carrying the record's ONE derived NEXT sentence
 * (Tesla law: the loudest line on the page after identity) + an optional
 * state chip (callback due / overdue / etc, caller-supplied so nothing is
 * fabricated here) + the ONE primary action + a ghost "door" affordance
 * that reaches the record's existing activity-logging surface.
 *
 * PRESENTATION-ONLY, a new composition over EXISTING truths (never a fork):
 * the sentence, the primary's label/href/handler, and the door's target are
 * ALL supplied by the caller from data it already computes
 * (LeadRecordCurrentTruth.nextLawfulAction; the existing lead-action-bar
 * emphasis key) -- this component adds no derivation and no new writer.
 *
 * LEAD-360-CONFORMANCE-B2 -- COMPOSER ON INVOCATION (F2 region 5 / F1 TIER 2
 * "COMPOSER ON INVOCATION"): when the caller supplies `composer`, BOTH the
 * actions row (primary + door) and the composer strip mount UNCONDITIONALLY
 * -- visibility toggles on the native `hidden` attribute keyed by `invoked`,
 * never a conditional unmount. This is the SAME idiom OverflowMenu already
 * proved for this repo's pinned react-dom/server static-render tests (a
 * conditionally-unmounted closed state is invisible to a static render and
 * therefore untestable/unpinnable -- the B1 LEARNING REPORT lesson). Never
 * open by default. The caller owns the invocation state and the composer's
 * own content; this primitive only lays out the two states so any RECORD
 * 360 adopter (Patient 360, the COC recovery console) gets the same
 * edge-card composer geometry for free.
 */

import type { ReactNode } from 'react';
import './signatures.css';

export interface NextActionEdgeProps {
  /** THE derived NEXT sentence (e.g. LeadRecordCurrentTruth.nextLawfulAction.label). */
  readonly sentence: string;
  /** Optional state fact beside the sentence (e.g. "Callback due 6 Aug 2026, 10:30 AM IST"). Omitted when nothing is recorded -- never a placeholder. */
  readonly stateChip?: ReactNode;
  /** THE one primary action for this record state (verb + object copy; reuses an existing lawful action -- never a new writer). */
  readonly primary?: ReactNode;
  /** The ghost "Log activity" door -- invokes the composer (or reaches the record's existing composer/work zone when no `composer` slot is wired). */
  readonly door?: ReactNode;
  /** The invoked composer's content (caller-owned; e.g. the lead disposition + note composer). Renders ONLY while `invoked` is true. */
  readonly composer?: ReactNode;
  /** True once the caller's door has been clicked. DEFAULT FALSE -- never open by default. While true, `primary`/`door` hide and `composer` shows. */
  readonly invoked?: boolean;
  readonly testId?: string;
}

export default function NextActionEdge({
  sentence,
  stateChip,
  primary,
  door,
  composer,
  invoked = false,
  testId,
}: NextActionEdgeProps): JSX.Element {
  const hasComposer = composer !== undefined;
  return (
    <section
      className="ac-nextedge"
      aria-label="Next action"
      data-testid={testId}
    >
      <div className="ac-nextedge__row">
        <div className="ac-nextedge__body">
          <span className="ac-nextedge__label">NEXT</span>
          <p className="ac-nextedge__sentence" data-testid="ac-nextedge-sentence">
            {sentence}
          </p>
          {stateChip ? (
            <span className="ac-nextedge__state">{stateChip}</span>
          ) : null}
        </div>
        {primary || door ? (
          <div
            className="ac-nextedge__actions"
            hidden={hasComposer && invoked ? true : undefined}
          >
            {primary ?? null}
            {door ?? null}
          </div>
        ) : null}
      </div>
      {hasComposer ? (
        <div
          className="ac-nextedge__composer"
          data-testid="ac-nextedge-composer"
          hidden={!invoked}
        >
          {composer}
        </div>
      ) : null}
    </section>
  );
}
